import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../services/api';
import './Login.css';

function Login({ onLoginSuccess }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState(() => localStorage.getItem('yw_remembered_email') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const navigate = useNavigate();

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 10) errors.push('At least 10 characters');
    if (pwd.length > 128) errors.push('No more than 128 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('At least 1 uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('At least 1 lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('At least 1 number');
    return errors;
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    if (!isLogin) setPasswordErrors(validatePassword(val));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLogin) {
      const errors = validatePassword(password);
      if (errors.length > 0) {
        setPasswordErrors(errors);
        return;
      }
    }

    setLoading(true);
    try {
      if (isLogin) {
        const response = await auth.login(email, password);
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('refreshToken', response.data.refreshToken);
        localStorage.setItem('yw_remembered_email', email);
        onLoginSuccess(user);
      } else {
        await auth.register(email, password);
        setRegistrationSuccess(true);
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.needsVerification) {
        setError('Please verify your email before logging in. Check your inbox for the verification link.');
      } else {
        setError(errorData?.error || 'Authentication failed');
      }
      console.warn('Login: auth failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="login-reg-success-wrap">
        <div className="login-reg-success-card">
          <div className="login-reg-success-emoji">✉️</div>
          <h2>Check Your Email!</h2>
          <p>
            We've sent a verification link to <strong>{email}</strong>
          </p>
          <p>
            Click the link in the email to verify your account and start using Yardray.
          </p>
          <button
            className="login-reg-success-btn"
            onClick={() => {
              setRegistrationSuccess(false);
              setIsLogin(true);
              setPassword('');
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-hero">
        <h1>Yardray</h1>
        <p>Your yard's permanent memory</p>
      </div>

      <div className="login-form-wrapper">
        <h2>{isLogin ? 'Login' : 'Create Account'}</h2>

        <form onSubmit={handleSubmit}>
          <div className="login-field">
            <label className="login-label">Email:</label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="login-field">
            <label className="login-label">Password:</label>
            <div className="login-password-wrap">
              <input
                className="login-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                required
              />
              <button
                type="button"
                className="login-show-hide-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {!isLogin && passwordErrors.length > 0 && (
              <div className="login-pw-errors">
                {passwordErrors.map((e, i) => (
                  <div key={i} className="login-pw-error-item">{e}</div>
                ))}
              </div>
            )}
            {!isLogin && password.length > 0 && passwordErrors.length === 0 && (
              <div className="login-pw-ok">Password meets requirements</div>
            )}
          </div>

          {error && (
            <div className="login-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="login-submit-btn"
          >
            {loading ? 'Loading...' : (isLogin ? 'Login' : 'Create Account')}
          </button>

          {isLogin && (
            <div className="login-forgot-wrap">
              <button
                type="button"
                className="login-forgot-btn"
                onClick={() => navigate('/forgot-password')}
              >
                Forgot password?
              </button>
            </div>
          )}
        </form>

        <p className="login-toggle-p">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button
            className="login-toggle-btn"
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setPasswordErrors([]);
              setPassword('');
            }}
          >
            {isLogin ? 'Register' : 'Login'}
          </button>
        </p>
      </div>
    </div>
  );
}

export default Login;
